import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Role } from '../models/user.model';
import { RestUtils } from './rest-utils';

export enum UserClickwrapStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
}

export interface UserClickwrapDetail {
  clickwrapTemplateId: string;
  description: string;
  status: UserClickwrapStatus;
  lastStatusUpdate: string;
}

export interface ClicktermTemplateSummary {
  clickwrapTemplateId: string;
  description: string;
  required: boolean;
  forRegistration: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LegalDocumentsService {
  private readonly http = inject(HttpClient);
  private readonly utils = new RestUtils();
  private readonly headers = new HttpHeaders();

  public getClickwrapTemplatesForUser() {
    const url = this.utils.buildUrl('ROLE/usr/clickwraps');
    const options = this.utils.getHttpHeaderOptions(this.headers, false);
    return this.http.get<UserClickwrapDetail[]>(url, options);
  }

  public verifyClickwrapSignature(clickwrapSignature: string) {
    const url = this.utils.buildUrl('ROLE/usr/clickwraps/verify', { clickwrapSignature });
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post(url, {}, options);
  }

  public getTemplateIDForRegistration(): Observable<string> {
    const url = this.utils.buildUrl('ROLE/usr/clickwrap-templates', { userType: Role.MAIN });
    const options = this.utils.getHttpHeaderOptions(this.headers, false);
    return this.http.get<ClicktermTemplateSummary[]>(url, options).pipe(
      map(templates => {
        const registrationTemplate = templates.find(t => t.forRegistration);
        return registrationTemplate ? registrationTemplate.clickwrapTemplateId : null;
      })
    );
  }

  public downloadClickwrapAgreement(clickwrapTemplateId: string): Observable<Blob> {
    const url = this.utils.buildUrl('ROLE/usr/clickwraps/certificate', { clickwrapTemplateId });
    const options = this.utils.getHttpHeaderOptions(this.headers, false);
    return this.http.get(url, { ...options, responseType: 'blob' });
  }

  public downloadUserClickwrapAgreementByAdmin(userId: number, clickwrapTemplateId: string): Observable<Blob> {
    const url = this.utils.buildUrl(`ROLE/usr/users/${userId}/clickwraps/certificate`, { clickwrapTemplateId });
    const options = this.utils.getHttpHeaderOptions(this.headers, false);
    return this.http.get(url, { ...options, responseType: 'blob' });
  }
}
